import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import { $mediaStatus } from '../stores/deviceStatus';
import { subscribeKeys } from 'nanostores';

export default class HwCarousel extends HTMLElement {
  constructor() {
    super();

    this.$swiper = this.querySelector('[data-swiper]') || this;
    this.$items = this.querySelectorAll('[data-slide]');
    this.$parent = this.closest('[data-carousel-parent]');
    this.$prevButton = this.$parent?.querySelector('[data-swiper-prev]');
    this.$nextButton = this.$parent?.querySelector('[data-swiper-next]');

    this.currentIndex = 0;
    this.swiper = null;

    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onSmallDetectionChange = this.onSmallDetectionChange.bind(this);
  }

  // =============================================================================
  // Lifecycle
  // =============================================================================
  connectedCallback() {
    this.bindEvents();

    this.unbindSmallDetection = subscribeKeys(
      $mediaStatus,
      ['isSmall'],
      this.onSmallDetectionChange
    );
    this.onSmallDetectionChange();
  }

  disconnectedCallback() {
    this.unbindEvents();
    this.destroySwiper();
    this.unbindSmallDetection?.();
  }

  updateButtons(isAtBeginning, isAtEnd) {
    const disablePrev = !!isAtBeginning;
    const disableNext = !!isAtEnd;

    if (this.$prevButton) {
      this.$prevButton.classList.toggle('swiper-button-disabled', disablePrev);
      this.$prevButton.toggleAttribute('disabled', disablePrev);
      this.$prevButton.setAttribute('aria-disabled', disablePrev ? 'true' : 'false');
    }

    if (this.$nextButton) {
      this.$nextButton.classList.toggle('swiper-button-disabled', disableNext);
      this.$nextButton.toggleAttribute('disabled', disableNext);
      this.$nextButton.setAttribute('aria-disabled', disableNext ? 'true' : 'false');
    }
  }

  // =============================================================================
  // Events
  // =============================================================================
  bindEvents() {
    this.$prevButton?.addEventListener('click', this.onPrevClick);
    this.$nextButton?.addEventListener('click', this.onNextClick);
  }

  unbindEvents() {
    this.$prevButton?.removeEventListener('click', this.onPrevClick);
    this.$nextButton?.removeEventListener('click', this.onNextClick);
  }

  onPrevClick() {
    if (this.swiper) {
      this.swiper.slidePrev();
      return;
    }
    const count = this.$items.length;
    if (!count) return;
    this.goTo((this.currentIndex - 1 + count) % count);
  }

  onNextClick() {
    if (this.swiper) {
      this.swiper.slideNext();
      return;
    }
    const count = this.$items.length;
    if (!count) return;
    this.goTo((this.currentIndex + 1) % count);
  }

  onSmallDetectionChange() {
    if ($mediaStatus.get().isSmall) {
      this.initSwiper();
    } else {
      this.destroySwiper();
      // Десктоп-режим: "ручной" активный слайд для ваших CSS-эффектов
      this.goTo(this.currentIndex || 0);
    }
  }

  initSwiper() {
    if (this.swiper) return;
    if (!this.$items.length) return;

    this.swiper = new Swiper(this.$swiper, {
      init: true,
      modules: [Navigation],
      // Чтобы всегда пролистывать и показывать по одному слайду
      slidesPerView: 1,
      spaceBetween: 18,
      loop: false,
      speed: $mediaStatus.get().isReducedMotion ? 0 : 400,
      on: {
        init: s => {
          // Swiper сам навешивает swiper-slide-active, но обновим ARIA и состояния кнопок
          this.$items.forEach((el, i) => el.setAttribute('aria-hidden', i !== s.activeIndex));
          this.updateButtons(s.isBeginning, s.isEnd);
        },
        slideChange: s => {
          this.currentIndex = s.activeIndex;
          this.$items.forEach((el, i) => el.setAttribute('aria-hidden', i !== s.activeIndex));
          this.updateButtons(s.isBeginning, s.isEnd);
        }
      }
    });

    // На случай, если init не успел отстрелять до первого рендера
    this.updateButtons(this.swiper.isBeginning, this.swiper.isEnd);
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  goTo(index) {
    const count = this.$items.length;
    if (count === 0 || index < 0 || index >= count) return;

    this.currentIndex = index;
    this.$items.forEach((el, i) => {
      el.classList.toggle('swiper-slide-active', i === index);
      el.setAttribute('aria-hidden', i !== index);
    });

    // Ручной режим (десктоп): дизейблим кнопки на краях
    const isAtBeginning = index === 0;
    const isAtEnd = index === count - 1;
    this.updateButtons(isAtBeginning, isAtEnd);
  }
}
